import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import PageLayout from '@/components/Layout/PageLayout';
import { DATE_RANGE_PRESETS } from '@/components/molecules/DateRangePicker/DateRangePresets';
import SomethingHappened from '@/components/SomethingHappened';
import { useCage, useCageName } from '@/contexts/cage';
import useCageFilter, { useCageScopedSearch } from '@/contexts/cage-filter-context';
import { isValidCageId } from '@/domain/cages';
import { useGetHealthOverview } from '@/domain/health';
import { useDateRangePicker } from '@/hooks/useDateRangePicker';
import { createCageUrl } from '@/utils/urlGenerator';

import {
    HEALTH_DEFAULT_DATE_RANGE_PRESET_ID,
    HEALTH_OVERVIEW_DATE_RANGE_URL_KEY,
} from '../components/healthDetailChartConstants';
import { HealthOverviewDateRangeProvider } from '../context/HealthOverviewDateRangeContext';

import HealthDashboardView from './HealthDashboardView';
import { isHealthOverviewDateChange } from './isHealthOverviewDateChange';

/** The status alerts always answer for the default window, not the picked overview range. */
function defaultOverviewRange(): { from: string | undefined; to: string | undefined } {
    const preset = DATE_RANGE_PRESETS.find(
        (item) => item.id === HEALTH_DEFAULT_DATE_RANGE_PRESET_ID
    );
    const dates = preset?.calculateDates();
    return { from: dates?.dateFrom, to: dates?.dateTo };
}

export default function HealthDashboardContainer() {
    const { t } = useTranslation('healthModule');
    const { cageId, client, location, loading: cageLoading } = useCage();
    const cageName = useCageName();
    const { daypartTag, selectedFishGroupId } = useCageFilter();
    const { dateFrom, dateTo, onDateChange } = useDateRangePicker({
        key: HEALTH_OVERVIEW_DATE_RANGE_URL_KEY,
        defaultPresetId: HEALTH_DEFAULT_DATE_RANGE_PRESET_ID,
    });
    const defaultRange = useMemo(() => defaultOverviewRange(), []);

    const sharedParams = useMemo(
        () => ({
            daypartTag,
            fishGroupId: selectedFishGroupId,
        }),
        [daypartTag, selectedFishGroupId]
    );

    const canQuery = isValidCageId(cageId) && !cageLoading;

    const statusQuery = useGetHealthOverview(
        cageId,
        { ...sharedParams, from: defaultRange.from, to: defaultRange.to },
        {
            enabled: canQuery && Boolean(defaultRange.from && defaultRange.to),
            staleTime: 5 * 60 * 1000,
        }
    );

    const overviewQuery = useGetHealthOverview(
        cageId,
        { ...sharedParams, from: dateFrom, to: dateTo },
        {
            enabled: canQuery && Boolean(dateFrom && dateTo),
            staleTime: 5 * 60 * 1000,
            // A new range is a new query key. Keep this selection's last answer so the page,
            // and any expanded row, stays mounted. Another pen, fish group, or daypart must not.
            placeholderData: (previous, previousQuery) =>
                isHealthOverviewDateChange({
                    previousCageId: previous?.data.cageId,
                    cageId,
                    fishGroupId: selectedFishGroupId,
                    daypartTag,
                    previousQueryKey: previousQuery?.queryKey,
                })
                    ? previous
                    : undefined,
        }
    );

    const healthBaseUrl =
        client?.id && location?.id ? `${createCageUrl(client.id, location.id, cageId)}/health` : '';
    // The detail pages answer for the same fish group (`fg`) as this overview.
    const detailSearch = useCageScopedSearch();

    const error = statusQuery.error ?? overviewQuery.error;
    const awaitingOverview = statusQuery.data === undefined || overviewQuery.data === undefined;
    const isInitialLoading =
        cageLoading || (awaitingOverview && (statusQuery.isLoading || overviewQuery.isLoading));

    if (error) {
        return (
            <PageLayout title={cageName} subtitle={t('routes.health')}>
                <SomethingHappened
                    error={error}
                    title={t('dashboard.loadError')}
                    onRetry={() => {
                        statusQuery.refetch();
                        overviewQuery.refetch();
                    }}
                    isRetrying={statusQuery.isFetching || overviewQuery.isFetching}
                />
            </PageLayout>
        );
    }

    return (
        <PageLayout title={cageName} subtitle={t('routes.health')}>
            <HealthOverviewDateRangeProvider value={{ dateFrom, dateTo, onDateChange }}>
                <HealthDashboardView
                    statusOverview={statusQuery.data}
                    overview={overviewQuery.data}
                    healthBaseUrl={healthBaseUrl}
                    detailSearch={detailSearch}
                    isInitialLoading={isInitialLoading}
                    isStatusLoading={statusQuery.isLoading || statusQuery.isFetching}
                    isOverviewLoading={overviewQuery.isLoading || overviewQuery.isFetching}
                />
            </HealthOverviewDateRangeProvider>
        </PageLayout>
    );
}
